// src/components/pages/benefaction/BenefactorList.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddCircleIcon } from "@hugeicons/react-pro";
import { useTranslation } from "../../../utils/i18nStub";
import { isAuthenticated } from "../../../services/authService";
import { listCurrentUserDonors, type DonorListItem } from "../../../services/donorService";
import AddDonorModal from "./AddDonorModal";
import Breadcrumbs from "../../../components/navigation/Breadcrumbs";
import type { Crumb } from "../../../components/navigation/Breadcrumbs";

function classNames(...xs: Array<string | undefined | false>) {
  return xs.filter(Boolean).join(" ");
}

const BenefactorList: React.FC = () => {
  const { t, i18n } = useTranslation(["BenefactorList"]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<DonorListItem[]>([]);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);

  // Auth gate – send them to benefactor login and bounce back here
  useEffect(() => {
    if (!isAuthenticated()) {
      const continueToken = encodeURIComponent(
        `${window.location.pathname}${window.location.search || ""}`
      );
      navigate(`/benefaction/login?c=${continueToken}`, { replace: true, state: { fromList: true } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load donors
  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const rows = await listCurrentUserDonors();
      setList(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setErr(e?.message || "فشل تحميل قائمة المساهمين.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasData = useMemo(() => (list?.length || 0) > 0, [list]);

  return (
    // ✅ Use shared DGA container (same side gaps as طلب تبرع)
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
      {/* Breadcrumbs and Back Button */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <Breadcrumbs className="flex-1" />
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm transition hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {t("back", "رجوع")}
        </button>
      </div>
  
      {/* Page header */}
      <div className="mb-4">{/* ✅ tighter than mb-6 */}
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
          {t("pageTitle", "قائمة المساهمين")}
        </h1>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          {t("pageSubtitle", "إدارة قائمة المساهمين المرتبطين بحسابك")}
        </p>
      </div>

      {/* Error banner (DGA style) */}
      {err && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200" // ✅ tighter than mb-5
        >
          {err || "فشل تحميل البيانات"}
        </div>
      )}

      {/* Action bar */}
      <div
        className={classNames(
          "mb-4 flex", // ✅ tighter than mb-5
          "justify-start" // Hardcoded for RTL
        )}
      >
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-2xl bg-sa-600 px-4 py-2 text-white shadow-sm transition hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
        >
          <AddCircleIcon size={18} color="currentColor" variant="stroke" className="shrink-0" />
          <span className="text-sm font-medium">{t("addDonor", "إضافة مُوكِّل")}</span>
        </button>
      </div>

      {/* Content states */}
      {loading ? (
        <div className="py-8 text-center text-neutral-600 dark:text-neutral-300">{/* ✅ tighter than py-12 */}
          {t("loading", "جاري التحميل...")}
        </div>
      ) : hasData ? (
        <div className="grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((row) => (
            <article
              key={row.id ?? `${row.name}-${row.nationalId}`}
              className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900"
            >
              {/* Card header */}
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                  {row.name || "-"}
                </h2>

                {/* Pill badge */}
                <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs font-medium text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                  {row.donorType || t("unknownType", "نوع غير معروف")}
                </span>
              </div>

              {/* Divider */}
              <div className="my-3 h-px bg-neutral-200 dark:bg-neutral-800" />

              {/* Table-like key-value pairs */}
              <div className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-3 gap-y-2 text-sm">
                <span className="text-neutral-600 dark:text-neutral-400 font-medium whitespace-nowrap">
                  {t("nationalId", "الهوية الوطنية")}:
                </span>
                <span className="text-neutral-900 dark:text-neutral-100 font-medium" dir="ltr">
                  {row.nationalId || "-"}
                </span>

                <span className="text-neutral-600 dark:text-neutral-400 font-medium whitespace-nowrap">
                  {t("phone", "الهاتف")}:
                </span>
                <span className="text-neutral-900 dark:text-neutral-100 font-medium" dir="ltr">
                  {row.phone || "-"}
                </span>

                <span className="text-neutral-600 dark:text-neutral-400 font-medium whitespace-nowrap">
                  {t("email", "البريد الإلكتروني")}:
                </span>
                <span className="text-neutral-900 dark:text-neutral-100 font-medium break-all" dir="ltr">
                  {row.email || "-"}
                </span>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center text-neutral-600 dark:text-neutral-300">{/* ✅ tighter than py-12 */}
          {t("noData", "لا توجد بيانات")}
        </div>
      )}

      {/* Modal (unchanged; overlay handled internally) */}
      <AddDonorModal
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={async () => {
          await load();
        }}
      />
    </div>
  );
};

export default BenefactorList;
